/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Public Server Settings page — `/server-settings`.
 *
 * Lets any visitor see the live gameplay rates / toggles / multipliers
 * for each of Chillet's Palworld servers (XP rate, Pal damage, day-time
 * speed, PvP toggles, etc.). Mirrors the data the bot's
 * `/server info` slash command used to show — now public so newcomers
 * can decide which realm fits their playstyle before joining.
 *
 * Backed by `GET /api/server-settings` (no auth, no host/port leakage).
 */
import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader } from "@/components/Atoms";
import { Server, RefreshCw, Loader2, Puzzle, Settings, ExternalLink } from "lucide-react";

// Curated NexusMods ID overlay — when we know the canonical mod ID
// for a folder name we link straight to its page. For everything
// else we fall back to a NexusMods *search* URL using the folder
// name as the query (the user explicitly approved best-guess
// matching). New entries: drop in `<folder_name>: <numeric_id>`.
//
// UE4SS internals (BPModLoaderMod, BPML_GenericFunctions etc.) point
// to the UE4SS GitHub release page instead — those aren't on Nexus
// and linking there would frustrate users searching for mod docs.
const NEXUS_MOD_IDS = {
  // Well-known Palworld Nexus mods. Verified by visiting each link;
  // add more as we identify them.
  PalSchema:           "1748",  // PalSchema — Pal data editing framework
  BountyTokenBooster:  "2224",  // user-provided example
};

const UE4SS_INTERNALS = new Set([
  "AdminCommands",         // ships with UE4SS
  "BPML_GenericFunctions", // Blueprint Mod Loader helper
  "BPModLoaderMod",        // Blueprint Mod Loader entry-point
  "ConsoleCommandsMod",
  "ConsoleEnablerMod",
  "CheatManagerEnablerMod",
  "ActorDumperMod",
  "ObjectDumperMod",
  "LiveView",
  "Keybinds",
  "SplitScreenMod",
  "jsbLuaProfilerMod",
]);

function modInfoUrl(name) {
  if (UE4SS_INTERNALS.has(name)) {
    return {
      href: "https://github.com/UE4SS-RE/RE-UE4SS",
      label: "UE4SS internal",
      title: "Ships with UE4SS itself — see the UE4SS repo for docs.",
    };
  }
  const id = NEXUS_MOD_IDS[name];
  if (id) {
    return {
      href: `https://www.nexusmods.com/palworld/mods/${id}`,
      label: "View on NexusMods",
      title: `Open the NexusMods page for ${name}`,
    };
  }
  // NexusMods doesn't expose a stable URL-based search (their
  // `?keywords=` param just lands on the unfiltered mods page).
  // Google's site-search is the most reliable shortcut: it surfaces
  // the matching mod page as the first result in 99% of cases.
  // We split CamelCase + replace underscores so multi-word folder
  // names like "BaseCampItemStacker" become "Base Camp Item Stacker"
  // — Google likes natural-language queries.
  const human = name
    .replace(/[_-]?v?\d+(\.\d+)*$/i, "")     // trailing version
    .replace(/([a-z])([A-Z])/g, "$1 $2")    // CamelCase → spaced
    .replace(/[_-]/g, " ")
    .trim();
  const query = `site:nexusmods.com/palworld ${human}`;
  return {
    href: `https://www.google.com/search?q=${encodeURIComponent(query)}`,
    label: "Find on NexusMods",
    title: `Google "${human}" within nexusmods.com — top result is usually the right mod page.`,
  };
}

function formatValue(v) {
  if (typeof v === "boolean") return v ? "✅ Yes" : "❌ No";
  if (typeof v === "number") {
    if (!Number.isInteger(v)) return `${v.toFixed(2)}x`;
    return String(v);
  }
  if (v == null || v === "") return "—";
  return String(v);
}

function valueClass(v) {
  if (typeof v === "boolean") return v ? "text-emerald-300" : "text-rose-300";
  if (typeof v === "number" && !Number.isInteger(v) && v !== 1) {
    return v > 1 ? "text-amber-300" : "text-teal-300";
  }
  return "text-[var(--bone)]";
}

export default function ServerSettings() {
  const toast = useToast();
  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState("");
  const [tab, setTab] = useState("settings");  // "settings" | "mods"
  const [data, setData] = useState(null);
  const [mods, setMods] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMods, setLoadingMods] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/server-settings");
        if (cancelled) return;
        const list = data.servers || [];
        setServers(list);
        if (list.length > 0) setSelected(list[0].name);
      } catch (e) { if (!cancelled) toast.error(apiError(e)); }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = async (name) => {
    if (!name) return;
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.get(`/server-settings/${encodeURIComponent(name)}`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
      setData({ error: apiError(e) });
    } finally {
      setLoading(false);
    }
  };

  const loadMods = async (name) => {
    if (!name) return;
    setLoadingMods(true);
    setMods(null);
    try {
      // SFTP crawl can take 5–15s on cold connections, so we let
      // axios's default timeout absorb the wait without showing an
      // error toast prematurely.
      const { data } = await api.get(`/server-settings/${encodeURIComponent(name)}/mods`, { timeout: 30000 });
      setMods(data);
    } catch (e) {
      toast.error(apiError(e));
      setMods({ error: apiError(e) });
    } finally {
      setLoadingMods(false);
    }
  };

  useEffect(() => {
    if (!selected) return;
    if (tab === "settings") load(selected);
    if (tab === "mods")     loadMods(selected);
  }, [selected, tab]);

  const totalSettings = useMemo(() =>
    (data?.categories || []).reduce((a, c) => a + (c.settings?.length || 0), 0)
  , [data]);

  const totalMods = useMemo(() => {
    if (!mods?.paths) return 0;
    return mods.paths.reduce((a, p) => a + (p.mods?.length || 0), 0);
  }, [mods]);

  return (
    <div className="space-y-4 fade-up" data-testid="public-server-settings-root">
      <PageHeader
        title="Server Settings & Mods"
        subtitle="Live gameplay rates and installed UE4SS / PalSchema mods for each of Chillet's realms."
      />

      {/* Tab switcher */}
      <div className="flex items-center gap-2" data-testid="server-settings-tabs">
        <button
          onClick={() => setTab("settings")}
          className={`btn ${tab === "settings" ? "btn-primary" : "btn-ghost"}`}
          data-testid="tab-settings"
        >
          <Settings size={14} /> Settings
        </button>
        <button
          onClick={() => setTab("mods")}
          className={`btn ${tab === "mods" ? "btn-primary" : "btn-ghost"}`}
          data-testid="tab-mods"
        >
          <Puzzle size={14} /> Mods
        </button>
      </div>

      <div className="glass-card p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="label flex items-center gap-2">
            <Server size={14} />
            {tab === "settings" ? "Live Gameplay Settings" : "Installed Server Mods"}
          </h2>
          <p className="text-xs text-[var(--muted)] mt-1">
            {tab === "settings"
              ? "Switch servers from the dropdown to compare XP rate, damage multipliers, and PvP rules."
              : "UE4SS + PalSchema mods currently loaded on this server (read straight from SFTP — same source the Discord /mods post command uses)."}
          </p>
        </div>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="input sm:w-72"
          data-testid="public-server-picker"
          disabled={loading || loadingMods || servers.length === 0}
        >
          {servers.length === 0 ? (
            <option value="">No servers configured</option>
          ) : (
            servers.map((s) => (
              <option key={s.name} value={s.name} disabled={tab === "settings" && !s.has_api}>
                {s.name}{tab === "settings" && !s.has_api ? " (no REST API)" : ""}
              </option>
            ))
          )}
        </select>
        <button
          onClick={() => (tab === "settings" ? load(selected) : loadMods(selected))}
          disabled={loading || loadingMods || !selected}
          className="btn btn-ghost"
          title="Refresh"
          data-testid="public-server-refresh"
        >
          {(loading || loadingMods) ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
      </div>

      {/* ── Settings tab ── */}
      {tab === "settings" && (
        <>
          {loading && !data && <Loader />}

          {data?.error && (
            <div className="glass-card p-6 border-l-2 border-rose-500/50">
              <p className="text-rose-300 font-mono text-sm">{data.error}</p>
            </div>
          )}

          {data && !data.error && (
            <>
              <div className="text-xs text-[var(--muted)] -mb-2" data-testid="public-settings-count">
                {totalSettings} settings · {data.categories.length} groups
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                {data.categories.map((cat) => (
                  <div key={cat.label} className="glass-card p-4" data-testid={`public-settings-cat-${cat.label.replace(/\s+/g, "-")}`}>
                    <h3 className="text-sm font-semibold mb-3 text-[var(--bone)]">{cat.label}</h3>
                    <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-sm">
                      {cat.settings.map((s) => (
                        <div key={s.key} className="contents">
                          <dt className="text-[var(--muted)] truncate" title={s.key}>{s.label}</dt>
                          <dd className={`font-mono text-right ${valueClass(s.value)}`}>{formatValue(s.value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Mods tab ── */}
      {tab === "mods" && (
        <>
          {loadingMods && !mods && <Loader />}
          {mods?.error && (
            <div className="glass-card p-6 border-l-2 border-rose-500/50">
              <p className="text-rose-300 font-mono text-sm">SFTP error: {mods.error}</p>
            </div>
          )}
          {mods && !mods.error && (
            <>
              <div className="text-xs text-[var(--muted)] -mb-2" data-testid="public-mods-count">
                {totalMods} mods installed across {mods.paths.filter(p => p.exists).length} mod path(s)
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                {mods.paths.map((p) => (
                  <ModPathCard key={p.path} path={p} />
                ))}
              </div>
              <div className="text-[11px] text-[var(--muted)] mt-2 italic">
                Tip: search NexusMods for the mod names above to find updates / patch notes.
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ModPathCard({ path }) {
  if (!path.exists) {
    return (
      <div className="glass-card p-4 border-l-2 border-amber-500/40" data-testid={`mod-path-missing-${path.label}`}>
        <h3 className="text-sm font-semibold mb-2 text-[var(--bone)] flex items-center gap-2">
          <Puzzle size={14} /> {path.label}
          <span className="text-[10px] uppercase tracking-wider text-amber-400/80 ml-auto">not found</span>
        </h3>
        <p className="text-xs text-[var(--muted)] font-mono break-all">{path.path}</p>
        <p className="text-xs text-[var(--muted)] mt-2">
          This directory isn&apos;t present on the server. Either no mods of this type are installed,
          or the path is non-standard (set <code>mod_path</code> / <code>palschema_path</code> in <code>sftp.yml</code>).
        </p>
      </div>
    );
  }
  return (
    <div className="glass-card p-4" data-testid={`mod-path-${path.label}`}>
      <h3 className="text-sm font-semibold mb-3 text-[var(--bone)] flex items-center gap-2">
        <Puzzle size={14} /> {path.label}
        <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] ml-auto">
          {path.mods.length} mod{path.mods.length !== 1 ? "s" : ""}
        </span>
      </h3>
      {path.mods.length === 0 && path.loose_files.length === 0 ? (
        <p className="text-xs text-[var(--muted)] italic">Empty.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {path.mods.map((m) => {
            const link = modInfoUrl(m.name);
            return (
              <li key={m.name} className="border-l border-white/10 pl-2.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono text-[var(--bone)]">{m.name}/</span>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.title}
                    className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-teal-300/80 hover:text-teal-200 transition-colors border border-teal-500/30 hover:border-teal-400/60 rounded-full px-1.5 py-0.5"
                    data-testid={`mod-link-${m.name}`}
                  >
                    {link.label} <ExternalLink size={9} />
                  </a>
                </div>
                {(m.dirs.length > 0 || m.files.length > 0) && (
                  <div className="text-[11px] text-[var(--muted)] font-mono mt-0.5 break-all">
                    {m.dirs.map((d) => `${d}/`).concat(m.files).slice(0, 8).join("  ·  ")}
                    {m.dirs.length + m.files.length > 8 && ` … +${m.dirs.length + m.files.length - 8} more`}
                  </div>
                )}
              </li>
            );
          })}
          {path.loose_files.length > 0 && (
            <li className="text-[11px] text-[var(--muted)] italic">
              Loose files: {path.loose_files.map((f) => `\`${f}\``).join(", ")}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
